import React, { useState, useEffect, useContext } from "react"
import LoadingPostRender from "components/LoadingScreensResources/LoadingPostRender"
import usePostRender from "hooks/usePostRender"
import SeriesDashboardWidget from "screens/HomeScreen/components/Widgets/SeriesDashboardWidget"
import OrdersDashboardWidget from "screens/HomeScreen/components/Widgets/OrdersDashboardWidget"
import SourcesDashboardWidget from "screens/HomeScreen/components/Widgets/SourcesDashboardWidget"
import OrdersByCourierWidget from "screens/HomeScreen/components/Widgets/OrdersByCourierWidget"
import { HiOutlineLocationMarker, HiOutlineCube, HiOutlineTruck, HiOutlineShoppingCart, HiOutlineChip, HiOutlineX } from "react-icons/hi"
import AuthContext from "store/context/PublicContext"
import { post_middleware_osrm_search } from "services/middleware_services"

const HomeScreen = () => {
  const { loadedPostRender } = usePostRender()
  const { user } = useContext(AuthContext) as any
  const token = user?.token

  const [storeInput, setStoreInput] = useState("")
  const [debouncedStoreInput, setDebouncedStoreInput] = useState("")
  const [globalCodVTASA, setGlobalCodVTASA] = useState<string | null>(null)
  const [globalSourceId, setGlobalSourceId] = useState<string | null>(null)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedStoreInput(storeInput)
    }, 500)
    return () => clearTimeout(handler)
  }, [storeInput])

  useEffect(() => {
    const fetchGlobalSource = async () => {
      const val = debouncedStoreInput.trim()
      if (!val) {
        setGlobalCodVTASA(null)
        return
      }
      try {
        const res = await post_middleware_osrm_search(
          "sources",
          { from: 0, size: 5, filters: { id: val } },
          token
        )
        const matchedSources = res?.data?.message?.records || []
        
        if (matchedSources.length > 0) {
          const codVTASA = matchedSources[0]?.custom?.codVTASA
          setGlobalCodVTASA(codVTASA ? String(codVTASA) : "NOT_FOUND_DUMMY_ID_OIM")
          setGlobalSourceId(String(matchedSources[0].id))
        } else {
          setGlobalCodVTASA("NOT_FOUND_DUMMY_ID_OIM")
          setGlobalSourceId("NOT_FOUND_DUMMY_ID_OIM")
        }
      } catch {
        setGlobalCodVTASA("NOT_FOUND_DUMMY_ID_OIM")
        setGlobalSourceId("NOT_FOUND_DUMMY_ID_OIM")
      }
    }
    fetchGlobalSource()
  }, [debouncedStoreInput, token])

  if (!loadedPostRender) return <LoadingPostRender />
  return (
    <div className="w-full h-full flex flex-col items-center justify-start overflow-y-auto">
      <div className="w-full flex flex-col justify-center items-center py-4 px-6 gap-4">
        {/* Dynamic Hero Header & Global Toolbar */}
        <div className="w-full relative overflow-hidden rounded-2xl bg-white shadow-sm p-6 lg:p-8 mb-2 border border-gray-100">
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2.5 bg-indigo-50/50 border border-indigo-100/50 rounded-xl shadow-sm">
                  <HiOutlineChip className="text-3xl text-indigo-600" />
                </div>
                <h1 className="text-4xl font-extrabold tracking-tight text-gray-900">
                  Xmonitor
                </h1>
              </div>
              <p className="text-gray-500 text-[13px] max-w-xl leading-relaxed mb-6 mt-1">
                Monitorea en tiempo real todas las operaciones, los movimientos recientes de inventario y mantén el control logístico mediante OMNIX IA.
              </p>
              
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-gray-200 shadow-sm text-gray-700 text-[11.5px] font-bold transition-all hover:bg-gray-50">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-ping absolute"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 relative"></span>
                  <HiOutlineShoppingCart className="text-sm text-indigo-500 ml-1" /> Órdenes
                </div>
                <div className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-gray-200 shadow-sm text-gray-700 text-[11.5px] font-bold transition-all hover:bg-gray-50">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-ping absolute"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 relative"></span>
                  <HiOutlineTruck className="text-sm text-indigo-500 ml-1" /> Logística
                </div>
                <div className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-gray-200 shadow-sm text-gray-700 text-[11.5px] font-bold transition-all hover:bg-gray-50">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-ping absolute"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 relative"></span>
                  <HiOutlineCube className="text-sm text-indigo-500 ml-1" /> Inventario
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky filter bar */}
        <div className="sticky top-0 z-20 w-full -mx-6 px-6 py-4 bg-white/95 backdrop-blur-lg border-b border-gray-200 shadow-md">
          <div className="w-full md:w-[420px] ml-auto relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <HiOutlineLocationMarker className="text-gray-400 group-focus-within:text-indigo-600 text-lg transition-colors" />
            </div>
            <input
              type="text"
              className="block w-full pl-11 pr-11 py-3 text-[14px] font-semibold border-2 border-gray-200 rounded-2xl shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 hover:border-gray-300 placeholder-gray-400 transition-all bg-gray-50/50 focus:bg-white text-gray-800"
              placeholder="Filtrar métricas por Tienda (Ej: 9001)..."
              value={storeInput}
              onChange={(e) => setStoreInput(e.target.value)}
            />
            {storeInput && (
              <button
                onClick={() => setStoreInput("")}
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-red-500 transition-colors"
              >
                <HiOutlineX className="text-lg" />
              </button>
            )}
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className={`w-full flex-1 grid grid-cols-1 md:grid-cols-2 ${user?.client === "entelperu" ? "lg:grid-cols-3" : ""} gap-6 pb-6`}>
          {/* Chart: full width */}
          <div className={`col-span-1 md:col-span-2 ${user?.client === "entelperu" ? "lg:col-span-3" : ""} h-[380px] overflow-visible`}>
            <OrdersByCourierWidget storeId={globalSourceId} />
          </div>

          <div className="border border-gray-100 shadow-sm bg-white overflow-hidden rounded-xl flex flex-col h-[500px] 2xl:h-[50vh] col-span-1">
            <OrdersDashboardWidget 
              title="Últimas órdenes procesadas" 
              sortField="_omnixStateMachine.currentState.date"
              sortOrder="desc"
              storeId={globalSourceId}
            />
          </div>

          {user?.client === "entelperu" && (
            <div className="border border-gray-100 shadow-sm bg-white overflow-hidden rounded-xl flex flex-col h-[500px] 2xl:h-[50vh] col-span-1">
              <SeriesDashboardWidget 
                title="Últimos movimientos de series" 
                actions={["CREATE", "UPDATE", "BULK_UPDATE"]} 
                sortField="creationDate"
                storeCodVTASA={globalCodVTASA}
              />
            </div>
          )}

          <div className="border border-gray-100 shadow-sm bg-white overflow-hidden rounded-xl flex flex-col h-[500px] 2xl:h-[50vh] col-span-1">
            <SourcesDashboardWidget 
              title="Últimas tiendas agregadas" 
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default HomeScreen
